import { Alert, Button, Card, CardBody, CardHeader } from '@heroui/react';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Patient } from '@/patient-app-common/types/Patient';
import { useAppSelector } from '@/redux/hooks';

import { DateModalDischarge } from '../../prompts/DateModal';
import { SetDischarge } from '../../prompts/SetDischarge';

interface PeriopCardProps {
  patient: Patient;
  surgeryDate: Dayjs | null;
  startOfDayInHospitalTz: Dayjs | null;
}

export function PeriopCard({
  patient,
  surgeryDate,
  startOfDayInHospitalTz,
}: PeriopCardProps) {
  const { t } = useTranslation('home');
  const [dateModal, toggleDateModal] = useState<boolean>(false);

  const { dischargePrompt } = useAppSelector((state) => state.app);

  const daysAfterSurgery = dayjs(startOfDayInHospitalTz).diff(
    dayjs(surgeryDate),
    'days'
  );

  const { hasPeriop, hasPostop, firstName, othersUsingApp } = patient;

  const headerText = othersUsingApp
    ? t('common.text.greeting_name_indirect', { firstName })
    : t('common.text.greeting_name', { firstName });

  useEffect(() => {
    const setDischargeDate = !hasPeriop && hasPostop && dischargePrompt;

    if (setDischargeDate) {
      toggleDateModal(true);
    }
  }, [dischargePrompt]);

  return (
    <>
      {hasPeriop ? (
        <DateModalDischarge
          isOpen={dateModal}
          onClose={() => toggleDateModal(false)}
          title={t('periopcard.button.set_discharge')}
        />
      ) : (
        <SetDischarge
          isOpen={dateModal}
          onClose={() => toggleDateModal(false)}
        />
      )}
      <Card shadow="none" className="bg-transparent px-0">
        <CardHeader className="px-0">
          <h1 className="text-2xl">{headerText}</h1>
        </CardHeader>
        <CardBody className="px-0">
          <h2 className="font-light">
            {t('periopcard.countdown', { count: daysAfterSurgery })}
          </h2>
          <p className="font-light">
            {hasPeriop && <p>{t('periopcard.has_periop.description')}</p>}
          </p>
        </CardBody>
      </Card>
      <Alert
        color="primary"
        description={t('periopcard.no_periop.description')}
        icon={<Icon icon="fa-solid:calendar-day" width="20" />}
        hideIconWrapper
        variant="faded"
      >
        <Button
          className="mt-4"
          color="primary"
          onPress={() => toggleDateModal(true)}
          size="sm"
        >
          {t('periopcard.button.set_discharge')}
        </Button>
      </Alert>
    </>
  );
}
